import React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import type { TrendArticle, TrendReport, TrendSignal, TrendStory } from '../types';
import { DISPLAY_FONT, TREND_COLORS, getArticleImage, getDesignSubCategoryLabel, getFallbackArticleImage } from '../trendTheme';

type TrendHeroCardProps = {
  report: TrendReport | null;
  story?: TrendStory | null;
  article: TrendArticle | null;
  signals?: TrendSignal[];
  onReadMore?: (article: TrendArticle) => void;
  onReadMoreStory?: (story: TrendStory) => void;
};

function formatHeroDate(value?: string | null): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function TrendHeroCard({
  report,
  story = null,
  article,
  onReadMore,
  onReadMoreStory,
}: TrendHeroCardProps) {
  const effectiveStory = report?.heroArticleId && article ? null : story;
  const effectiveArticle = article;
  const imageUrl = getArticleImage(effectiveStory?.heroImage || effectiveArticle?.heroImage, effectiveStory?.category || effectiveArticle?.category);
  const fallbackImageUrl = getFallbackArticleImage(effectiveStory?.category || effectiveArticle?.category);
  const articleIntro = effectiveArticle?.articleSections?.articleIntro || effectiveArticle?.excerpt || effectiveArticle?.whyItMatters || report?.intro || '';
  const title = effectiveStory?.title || effectiveArticle?.title || report?.title || 'TRENDRAPPORT';
  const subtitle = effectiveStory?.summary || effectiveStory?.whatItMeansForUs || articleIntro;
  const clickable = !!(effectiveStory ? onReadMoreStory : effectiveArticle && onReadMore);
  const articleSourceName = effectiveArticle?.sourceReferences?.[0]?.sourceName || '';
  const articleSourceDate = formatHeroDate(effectiveArticle?.sourceReferences?.[0]?.publishedAt || effectiveArticle?.publishedAt || null);

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        minHeight: { xs: 340, md: 420 },
        display: 'flex',
        alignItems: 'flex-end',
        cursor: clickable ? 'pointer' : 'default',
      }}
      onClick={() => {
        if (effectiveStory) {
          onReadMoreStory?.(effectiveStory);
          return;
        }
        if (effectiveArticle) {
          onReadMore?.(effectiveArticle);
        }
      }}
    >
      <Box
        component="img"
        src={imageUrl}
        alt=""
        onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
          if (event.currentTarget.src !== fallbackImageUrl) {
            event.currentTarget.src = fallbackImageUrl;
          }
        }}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(7,47,83,0.92) 0%, rgba(7,47,83,0.45) 50%, rgba(7,47,83,0.08) 100%)',
        }}
      />
      <Box sx={{ position: 'relative', p: { xs: 3, md: 5 }, maxWidth: 720 }}>
        <Chip
          label={effectiveStory ? 'VECKANS HUVUDSTORY' : 'VECKANS HUVUDSPANING'}
          size="small"
          sx={{
            mb: 2,
            bgcolor: TREND_COLORS.gold,
            color: TREND_COLORS.navy,
            fontWeight: 800,
            fontSize: '0.65rem',
            letterSpacing: '0.15em',
            borderRadius: 1,
          }}
        />
        <Typography
          variant="h3"
          sx={{
            fontFamily: DISPLAY_FONT,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 700,
            color: '#fff',
            lineHeight: 1.15,
            mb: 2,
            fontSize: { xs: '1.6rem', md: '2.2rem' },
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255,255,255,0.88)',
            lineHeight: 1.7,
            fontStyle: 'italic',
            mb: 2.5,
            maxWidth: 560,
          }}
        >
          {subtitle}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {effectiveStory
            ? [
              ...(effectiveStory.signalLabels || []).slice(0, 3),
              effectiveStory.designSubCategory ? getDesignSubCategoryLabel(effectiveStory.designSubCategory) : '',
            ].filter(Boolean).map((label) => (
              <Chip
                key={label}
                label={label}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  backdropFilter: 'blur(4px)',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                }}
              />
            ))
            : [
              effectiveArticle?.category ? effectiveArticle.category.toUpperCase() : '',
              articleSourceName,
              articleSourceDate,
            ].filter(Boolean).map((label) => (
              <Chip
                key={label}
                label={label}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  backdropFilter: 'blur(4px)',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                }}
              />
            ))}
        </Stack>
        {effectiveStory && onReadMoreStory && (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              bgcolor: TREND_COLORS.gold,
              color: TREND_COLORS.navy,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: '0.8rem',
              px: 3,
              '&:hover': { bgcolor: '#D4B67A' },
            }}
          >
            Öppna story
          </Button>
        )}
        {!effectiveStory && effectiveArticle && onReadMore && (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              bgcolor: TREND_COLORS.gold,
              color: TREND_COLORS.navy,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: '0.8rem',
              px: 3,
              '&:hover': { bgcolor: '#D4B67A' },
            }}
          >
            Öppna artikel
          </Button>
        )}
      </Box>
    </Box>
  );
}
